import { getCachedStylistPhotoCategories } from "@/lib/api/stylistPhotoCategories";
import { photoEngagementMetadata } from "@/lib/api/photoEngagement";
import { adaptMemberPortfolio } from "@/lib/api/memberPortfolio";
import { sumCounts } from "@/lib/api/crossBusiness";
import {
  OPENINGS_PASADENA_SCHEDULE_ID,
  OPENINGS_SAN_GABRIEL_SCHEDULE_ID,
  OPENINGS_ARTDISTRICT_SCHEDULE_ID,
  OPENINGS_MID_WILSHIRE_SCHEDULE_ID,
  OPENINGS_SAWTELLE_SCHEDULE_ID,
  OPENINGS_EDITION_STUDIO_SCHEDULE_ID,
} from "@/consts";
import {
  getBusinessForMemberId,
  getEditionUserPathIdFromMemberId,
  getStylistMemberId,
  STYLIST_CANONICAL_BUSINESS,
  STYLISTS_BY_BUSINESS,
} from "@/consts/editionStudio";
import { BUSINESSES, BUSINESS_KEYS } from "@/consts/businesses";
import { resolveEffectiveMinBookingNotice } from "@/lib/bookingLimits";
import { STYLIST_SERVICES, stylistPhotoKey, type StylistCardData, type StylistDirectory, type StylistTag } from "@/lib/stylists";
import {
  createPromiseTtlCache,
  type PromiseTtlCache,
} from "@/lib/promiseTtlCache";
import { notFound } from "next/navigation";
import { OpeningsJson } from "./openingsClient";
import { parseScheduleAddress as parseAddressParts } from "./scheduleAddress";
import {
  RESCHEDULE_REFUSAL_MESSAGES,
  rescheduleRefusalFromError,
} from "./rescheduleRefusal";
import {
  DEFAULT_EDITION_BUSINESS,
  EDITION_BUSINESS_HANDLES,
  resolveOpeningsBusiness,
  type EditionBusinessKey,
} from "./openingsBusiness";
import type {
  AppointmentHistoryData,
  BusinessService,
  AppointmentReviewData,
  AppointmentReviewService,
  ICreateAppointmentArgs,
  ICreatePaymentMethodArgs,
  IRescheduleAppointmentArgs,
  IServiceRequestArgs,
  Location,
  LocationData,
  LocationOpeningsData,
  PagedResult,
  PhotosResponse,
  Service,
  ServiceStylistData,
  ServiceOption,
  UserData,
  UserExperienceResponse,
  UserOpeningsData,
} from "./types";
import type {
  OpeningsMember,
  OpeningsMemberOpenings,
  OpeningsAppointmentHistory,
  OpeningsAppointmentReview,
  OpeningsCreateAppointmentResult,
  OpeningsCustomerGate,
  OpeningsMemberDetails,
  OpeningsMemberDetailsPhoto,
  OpeningsMemberTag,
  OpeningsNextAvailabilityItem,
  OpeningsPhotoItem,
  OpeningsSchedule,
  OpeningsScheduleHour,
  OpeningsService,
  OpeningsServiceOption,
  OpeningsUserPage,
  OpeningsScheduleDetail,
} from "./openingsTypes";

type OpeningsLocationFallback = {
  name: string;
  address: string;
  city: string;
  state: string;
  zipcode: string;
  lat: number;
  lng: number;
  phone: string;
  map: string;
  /** Marketing copy Openings does not provide, shown on the location page. */
  description?: string;
  /**
   * Time-boxed copy shown before `description` through `until` (inclusive,
   * `YYYY-MM-DD` in Los Angeles), then dropped on its own — e.g. a soft
   * opening notice nobody has to remember to delete.
   */
  notice?: { text: string; until: string };
};

export type OpeningsLocationConfig = {
  /** Eddie's Edition location pathId (route segment). */
  pathId: string;
  /** Openings schedule slug — resolves the backing schedule from the API. */
  slug: string;
  /** Last-known schedule ID, used only to recognize legacy booking context. */
  scheduleId: string;
  /** Which Openings business owns this location. */
  business: EditionBusinessKey;
  /** Static fallbacks for fields Openings may not provide. */
  fallback: OpeningsLocationFallback;
};

/**
 * Registry of Eddie's Edition locations migrated to the Openings API.
 * To migrate another location: add its slug, fallback schedule ID, and route
 * metadata here. Staff assignment is read from the live schedule roster.
 */
export const OPENINGS_LOCATIONS: OpeningsLocationConfig[] = [
  {
    pathId: "pasadena",
    slug: "pasadena",
    scheduleId: OPENINGS_PASADENA_SCHEDULE_ID,
    business: "eddies",
    fallback: {
      name: "Pasadena",
      address: "914 Union St",
      city: "Pasadena",
      state: "CA",
      zipcode: "91106",
      lat: 34.1475969,
      lng: -118.1314463,
      phone: "626-888-8052",
      map: "https://maps.google.com/?q=914%20Union%20St%2C%20Pasadena%2C%20CA%2091106",
    },
  },
  {
    pathId: "san-gabriel",
    slug: "san-gabriel",
    scheduleId: OPENINGS_SAN_GABRIEL_SCHEDULE_ID,
    business: "eddies",
    fallback: {
      name: "San Gabriel",
      address: "123 N San Gabriel Blvd",
      city: "San Gabriel",
      state: "CA",
      zipcode: "91775",
      lat: 34.1042892,
      lng: -118.0912548,
      phone: "626-888-8052",
      map: "https://g.page/eddie-s-edition-san-gabriel?share",
    },
  },
  {
    pathId: "dtla",
    slug: "arts-district",
    scheduleId: OPENINGS_ARTDISTRICT_SCHEDULE_ID,
    business: "eddies",
    fallback: {
      name: "Arts District",
      address: "1320 E 7th St #136",
      city: "Los Angeles",
      state: "CA",
      zipcode: "90021",
      lat: 34.0518,
      lng: -118.2426,
      phone: "",
      map: "https://maps.google.com/?q=1320%20E%207th%20St%20Los%20Angeles%20CA%2090021",
    },
  },
  {
    pathId: "mid-wilshire",
    slug: "mid-wilshire",
    scheduleId: OPENINGS_MID_WILSHIRE_SCHEDULE_ID,
    business: "eddies",
    fallback: {
      name: "Mid Wilshire",
      address: "4045 Wilshire Blvd",
      city: "Los Angeles",
      state: "CA",
      zipcode: "90010",
      lat: 34.0618602,
      lng: -118.3160178,
      phone: "",
      map: "https://maps.google.com/?q=4045%20Wilshire%20Blvd%20Los%20Angeles%20CA%2090010",
    },
  },
  {
    pathId: "sawtelle",
    slug: "sawtelle",
    scheduleId: OPENINGS_SAWTELLE_SCHEDULE_ID,
    business: "eddies",
    fallback: {
      name: "Sawtelle",
      address: "11668 W Olympic Blvd #208",
      city: "Los Angeles",
      state: "CA",
      zipcode: "90064",
      lat: 34.034157,
      lng: -118.4473271,
      phone: "626-998-3852",
      map: "https://maps.app.goo.gl/NdcRqTvycxZXdi659",
      notice: {
        text: "Sawtelle is in soft opening through November, so hours and availability are limited while we settle in. Thanks for being one of our first visitors.",
        until: "2026-11-30",
      },
    },
  },
  {
    pathId: "edition-studio",
    slug: "studio",
    scheduleId: OPENINGS_EDITION_STUDIO_SCHEDULE_ID,
    business: "edition-studio",
    fallback: {
      name: "Edition Studio",
      address: "327 S San Gabriel Blvd",
      city: "San Gabriel",
      state: "CA",
      zipcode: "91776",
      lat: 34.0980627,
      lng: -118.091168,
      phone: "213-800-1470",
      map: "https://maps.google.com/?q=327%20S%20San%20Gabriel%20Blvd%2C%20San%20Gabriel%2C%20CA%2091776",
      description:
        "Edition Studio is a studio-style space where our most experienced team members offer a wider range of services. Same roots as Eddie's Edition, with a more personalized experience.",
    },
  },
];

const matchesBusiness = (
  config: OpeningsLocationConfig,
  business?: EditionBusinessKey,
) => !business || config.business === business;

// Populated whenever the live directory resolves a schedule slug to its
// current ID. This lets later synchronous booking guards recognize a rotated
// schedule ID without baking that ID into the source tree.
const discoveredConfigByScheduleId = new Map<string, OpeningsLocationConfig>();

const getConfigByLocationId = (
  locationId: string | null | undefined,
  business?: EditionBusinessKey,
): OpeningsLocationConfig | undefined =>
  locationId
    ? (() => {
        const discovered = discoveredConfigByScheduleId.get(locationId);
        if (discovered && matchesBusiness(discovered, business)) {
          return discovered;
        }
        return OPENINGS_LOCATIONS.find(
          (config) =>
            matchesBusiness(config, business) &&
            (config.pathId === locationId ||
              config.slug === locationId ||
              config.scheduleId === locationId),
        );
      })()
    : undefined;

export type StylistMembership = {
  business: EditionBusinessKey;
  memberId: string;
};

/**
 * Every business identity a stylist holds. A human working at two businesses
 * has two Openings TeamMember rows with different ids. Location membership is
 * deliberately not represented here; it comes from live schedule rosters.
 */
export const getStylistMemberships = (pathId: string): StylistMembership[] => {
  const key = pathId.toLowerCase();
  return BUSINESS_KEYS.flatMap((business) => {
    const memberId = getStylistMemberId(key, business);
    return memberId ? [{ business, memberId }] : [];
  });
};

/**
 * Which business's profile owns `/stylists/<pathId>`.
 *
 * Prefers the explicit `STYLIST_CANONICAL_BUSINESS` entry when one exists.
 * Every pathId with memberships in 2+ distinct businesses is REQUIRED to have
 * one — that is enforced at build time by the invariant tests in
 * `src/lib/api/__tests__/stylistIdentity.test.ts`, not here.
 *
 * Falls back, when no entry is present, to: `eddies` if the stylist has a
 * membership there, otherwise their first membership's business. This
 * fallback is deliberately tolerant, not an enforcement path — this function
 * backs a read path behind `/stylists/<pathId>`, and a defensible guess here
 * is better than a 500 on a profile page. Build-time enforcement, runtime
 * tolerance.
 */
export const getCanonicalBusinessForStylist = (
  pathId: string,
): EditionBusinessKey | undefined => {
  const key = pathId.toLowerCase();
  const override = STYLIST_CANONICAL_BUSINESS[key];
  if (override) return override;

  const memberships = getStylistMemberships(key);
  if (memberships.length === 0) return undefined;
  return memberships.some((m) => m.business === "eddies")
    ? "eddies"
    : memberships[0].business;
};

/** Every Openings schedule ID registered for one business. */
export const getAllScheduleIds = (business: EditionBusinessKey): string[] =>
  OPENINGS_LOCATIONS.filter((config) => config.business === business).map(
    (config) => config.scheduleId,
  );

type RegisteredLocationRoute = {
  config: OpeningsLocationConfig;
  /** Current host schedule id resolved from the business's live schedule list. */
  scheduleId: string;
  schedule: OpeningsSchedule;
};

type LiveLocationRoute = RegisteredLocationRoute & {
  detail: OpeningsScheduleDetail;
};

type BusinessRouteDirectory = {
  businessId?: string;
  routes: RegisteredLocationRoute[];
  hadFailure: boolean;
};

type BusinessRouteDetails = {
  businessId?: string;
  routes: LiveLocationRoute[];
  hadFailure: boolean;
};

type LiveStylistRoutes = BusinessRouteDetails & StylistMembership;

type LiveLocationResolution = {
  businessId?: string;
  route?: LiveLocationRoute;
  hadFailure: boolean;
};

const ROUTER_CACHE_MS = 5 * 60 * 1000;
const routeDetailsCache = createPromiseTtlCache<
  EditionBusinessKey,
  BusinessRouteDetails
>({ ttlMs: ROUTER_CACHE_MS });
const locationRouteCache = createPromiseTtlCache<
  string,
  LiveLocationResolution
>({ ttlMs: ROUTER_CACHE_MS });

const loadBusinessRouteDirectory = async (
  businessKey: EditionBusinessKey,
): Promise<BusinessRouteDirectory> => {
  let business: Awaited<ReturnType<typeof resolveOpeningsBusiness>>;
  try {
    business = await resolveOpeningsBusiness(businessKey);
  } catch {
    return { routes: [], hadFailure: true };
  }

  const configs = OPENINGS_LOCATIONS.filter(
    (config) => config.business === businessKey,
  );
  let listedSchedules: OpeningsSchedule[];
  try {
    ({ items: listedSchedules } = await OpeningsJson.getBusinessSchedules(
      business.id,
    ));
  } catch {
    return {
      businessId: business.id,
      routes: [],
      hadFailure: true,
    };
  }

  // A successful empty list is authoritative: no registered location remains
  // bookable in this business. A failed list also stays empty-but-uncertain;
  // the router never guesses from the last-known IDs above.
  if (listedSchedules.length === 0) {
    return {
      businessId: business.id,
      routes: [],
      hadFailure: false,
    };
  }

  let hadFailure = false;
  const routes = configs.flatMap((config) => {
    const slugCandidates = listedSchedules.filter(
      (schedule) => schedule.slug === config.slug,
    );
    // The slug is the stable live route. Last-known IDs are accepted only by
    // synchronous legacy booking-context recognition, never as a live read
    // target. During an ID rotation both rows can briefly coexist, and the
    // canonical slug therefore wins without consulting the stale ID.
    const candidates = slugCandidates;
    if (candidates.length === 0) {
      // A missing slug is a normal decommissioned route unless the API still
      // presents the last-known row under some other/absent slug. That shape
      // is inconsistent and must be retryable rather than relabeled.
      if (
        listedSchedules.some((schedule) => schedule.id === config.scheduleId)
      ) {
        hadFailure = true;
      }
      return [];
    }
    if (candidates.length !== 1 || candidates[0].salonId !== business.id) {
      // Duplicate matches, foreign tenants, and an absent tenant assertion are
      // upstream inconsistencies. None may become a guessed booking target.
      hadFailure = true;
      return [];
    }

    const [schedule] = candidates;
    return [{ config, scheduleId: schedule.id, schedule }];
  });

  return { businessId: business.id, routes, hadFailure };
};

const cached = <Key, T>(
  cache: PromiseTtlCache<Key, T>,
  key: Key,
  load: () => Promise<T>,
  shouldCache: (result: T) => boolean,
): Promise<T> => {
  // Server reads already use Next's 300-second Data Cache. A second module
  // TTL can extend stale rosters toward ten minutes around a revalidation
  // boundary; keep this short-lived router cache only in the browser.
  if (process.env.NODE_ENV === "test" || typeof window === "undefined") {
    return load();
  }
  return cache.get(key, load, shouldCache);
};

const getBusinessRouteDirectory = (
  business: EditionBusinessKey,
): Promise<BusinessRouteDirectory> => loadBusinessRouteDirectory(business);

const loadBusinessRouteDetails = async (
  business: EditionBusinessKey,
): Promise<BusinessRouteDetails> => {
  const directory = await getBusinessRouteDirectory(business);
  if (!directory.businessId) {
    return { businessId: undefined, routes: [], hadFailure: true };
  }

  let hadFailure = directory.hadFailure;
  const settled = await Promise.all(
    directory.routes.map(async (route) => {
      try {
        const rawDetail = await OpeningsJson.getScheduleDetail(
          route.scheduleId,
        );
        if (
          rawDetail.schedule?.id !== route.scheduleId ||
          rawDetail.schedule?.salonId !== directory.businessId
        ) {
          hadFailure = true;
          return null;
        }
        const detail = normalizeScheduleDetail(rawDetail);
        discoveredConfigByScheduleId.set(route.scheduleId, route.config);
        return { ...route, detail };
      } catch (error) {
        console.error(error);
        hadFailure = true;
        return null;
      }
    }),
  );

  return {
    businessId: directory.businessId,
    routes: settled.filter(
      (route): route is LiveLocationRoute => route !== null,
    ),
    hadFailure,
  };
};

const getBusinessRouteDetails = (
  business: EditionBusinessKey,
): Promise<BusinessRouteDetails> =>
  cached(
    routeDetailsCache,
    business,
    () => loadBusinessRouteDetails(business),
    (result) => !result.hadFailure,
  );

const normalizeScheduleDetail = (
  detail: OpeningsScheduleDetail,
): OpeningsScheduleDetail => ({
  ...detail,
  members: detail.members ?? [],
  services: detail.services ?? [],
});

const loadLiveLocationRoute = async (
  config: OpeningsLocationConfig,
): Promise<LiveLocationResolution> => {
  const directory = await getBusinessRouteDirectory(config.business);
  const registered = directory.routes.find(
    (candidate) => candidate.config === config,
  );
  if (!directory.businessId || !registered) {
    return {
      businessId: directory.businessId,
      hadFailure: directory.hadFailure,
    };
  }

  try {
    const rawDetail = await OpeningsJson.getScheduleDetail(
      registered.scheduleId,
    );
    if (
      rawDetail.schedule?.id !== registered.scheduleId ||
      rawDetail.schedule?.salonId !== directory.businessId
    ) {
      return { businessId: directory.businessId, hadFailure: true };
    }
    const detail = normalizeScheduleDetail(rawDetail);
    discoveredConfigByScheduleId.set(registered.scheduleId, config);
    return {
      businessId: directory.businessId,
      route: { ...registered, detail },
      hadFailure: directory.hadFailure,
    };
  } catch (error) {
    console.error(error);
    return { businessId: directory.businessId, hadFailure: true };
  }
};

const getLiveLocationRoute = (
  config: OpeningsLocationConfig,
): Promise<LiveLocationResolution> =>
  cached(
    locationRouteCache,
    `${config.business}:${config.pathId}`,
    () => loadLiveLocationRoute(config),
    (result) => Boolean(result.route) && !result.hadFailure,
  );

/** Resolve current locations from Openings, then filter by its live roster. */
const getLiveStylistRoutes = async (
  membership: StylistMembership,
): Promise<LiveStylistRoutes> => {
  const directory = await getBusinessRouteDetails(membership.business);
  return {
    ...membership,
    ...directory,
    routes: directory.routes.filter((route) =>
      (route.detail.members ?? []).some(
        (member) => getOpeningsMemberId(member) === membership.memberId,
      ),
    ),
  };
};

/**
 * A location identifier → its owning business. Accepts anything
 * `getConfigByLocationId` accepts: a route `pathId`, an Openings `slug`, or a
 * registry `scheduleId`. Undefined for a location the registry does not know
 * (a coming-soon entry, a decommissioned URL) — callers pick their own
 * default, and read paths may default to Eddie's while write paths must not.
 */
export const resolveBusinessForLocation = (
  locationId: string | null | undefined,
): EditionBusinessKey | undefined =>
  getConfigByLocationId(locationId)?.business;

/**
 * Registered schedule id → owning business.
 *
 * There is deliberately NO fail-closed `requireBusinessForSchedule` sibling.
 * One existed and was deleted: its implied contract ("give me a booking's
 * scheduleId and I'll give you its business") is false for the location-page
 * path, where `adaptOpeningsMember` sets `schedule._id` to the MEMBER'S own
 * schedule id and stores the registry id as `locationId` (ruling R11). A
 * future write path reaching for that obvious-sounding name would have thrown
 * on most of the site's bookings. Write paths use
 * `requireBusinessForAppointment`, which resolves across every identifier.
 */
export const resolveBusinessForSchedule = (
  scheduleId: string | null | undefined,
): EditionBusinessKey | undefined =>
  getConfigByLocationId(scheduleId)?.business;

/**
 * Resolve which location config produced an availability item. The
 * next-availability endpoint returns the member-specific schedule ID (not the
 * business schedule ID we query with), so we match on the schedule title, which
 * mirrors each location's display name. Single-location stylists always resolve
 * to their only config.
 */
const resolveOpeningRoute = (
  schedule: OpeningsNextAvailabilityItem["schedule"],
  candidates: LiveLocationRoute[],
): LiveLocationRoute | undefined => {
  const byId = candidates.filter(
    (route) =>
      route.scheduleId === schedule.id ||
      route.detail.schedule.id === schedule.id,
  );
  if (byId.length === 1) return byId[0];

  const normalized = schedule.title?.trim().toLowerCase();
  const byTitle = candidates.filter(
    (route) =>
      route.detail.schedule.title?.trim().toLowerCase() === normalized ||
      route.config.fallback.name.toLowerCase() === normalized,
  );
  return byTitle.length === 1 ? byTitle[0] : undefined;
};

const normalizeLocationKey = (value: string | null | undefined) =>
  value?.trim().toLowerCase().replace(/\s+/g, "-") ?? "";

/**
 * salonId → business key: the review wire's own business-scope assertion
 * (verified present on the wire — see `OpeningsAppointmentReview.salonId`),
 * authoritative over the registry chain and title heuristics. It resolves
 * against each registered business record and fails closed when none matches;
 * a present system-of-record scope must never degrade to a guessed brand.
 */
const businessKeyForSalonId = async (
  salonId: string,
): Promise<EditionBusinessKey> => {
  for (const key of BUSINESS_KEYS) {
    try {
      const business = await resolveOpeningsBusiness(key);
      if (business.id === salonId) return key;
    } catch (error) {
      // A sibling business can still resolve the authoritative id, so keep
      // looking. If none does, the generic error below fails the review closed
      // instead of guessing from a title after an incomplete lookup.
      console.error(error);
    }
  }
  throw new Error(
    "Unable to verify the appointment business. Please try again.",
  );
};

/**
 * Best-effort BRAND (not location) resolution from a schedule title, for read
 * paths where no registry identifier matches — "Edition Studio - SG" matches
 * no registry key, but it carries its brand as a prefix, the same prefix rule
 * `buildLocationBranding` applies to page titles. Longest display name wins
 * so overlapping prefixes cannot misresolve. Never used for anything with a
 * write-path or location-details consequence.
 */
const businessKeyForScheduleTitle = (
  title: string | null | undefined,
): EditionBusinessKey | undefined => {
  const normalized = title?.trim().toLowerCase();
  if (!normalized) return undefined;
  return (Object.keys(BUSINESSES) as EditionBusinessKey[])
    .sort(
      (a, b) =>
        BUSINESSES[b].branding.displayName.length -
        BUSINESSES[a].branding.displayName.length,
    )
    .find((key) =>
      normalized.startsWith(BUSINESSES[key].branding.displayName.toLowerCase()),
    );
};

/**
 * A location's display name (usually an Openings schedule title) → its config.
 *
 * Matches the `pathId`, the Openings `slug` AND the registry's own
 * `fallback.name`, because a schedule title matches whichever of the three
 * happens to look like it. Checking `pathId` alone silently missed every
 * location whose title is not its route segment — `dtla`'s schedule is titled
 * "Arts District", which is its slug and its fallback name but never its
 * pathId — so those bookings reached the write-path guard with one fewer
 * resolvable identifier than they actually had.
 */
export const getConfigByLocationName = (
  locationName: string | null | undefined,
  business?: EditionBusinessKey,
): OpeningsLocationConfig | undefined => {
  const normalized = normalizeLocationKey(locationName);
  return normalized
    ? OPENINGS_LOCATIONS.find(
        (config) =>
          matchesBusiness(config, business) &&
          (config.pathId === normalized ||
            config.slug === normalized ||
            normalizeLocationKey(config.fallback.name) === normalized),
      )
    : undefined;
};

const SERVICE_CATEGORY_BY_TITLE: Record<string, number> = {
  "men's haircut": 1,
  "women's haircut": 2,
  "men's perm": 3,
  color: 4,
  "kid's haircut": 5,
  "beard trim": 9,
  "down perm": 10,
};

/** Normalize human-authored catalog labels without erasing meaningful words. */
const normalizeTitle = (title: string) =>
  title
    .normalize("NFKC")
    .replace(/[\u2018\u2019\u02bc`\u00b4]/g, "'")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const normalizePhoneNumber = (phoneNumber: string) => {
  const trimmed = phoneNumber.trim();
  const digits = trimmed.replace(/[^0-9]/g, "");
  return trimmed.startsWith("+") ? `+${digits}` : digits;
};

const getServiceCategory = (title: string) =>
  SERVICE_CATEGORY_BY_TITLE[normalizeTitle(title)] ?? 0;

const getOpeningsMemberId = (
  member: Pick<OpeningsMember, "teamMemberId" | "userId" | "id">,
) => member.teamMemberId ?? member.userId ?? member.id;

const parseScheduleAddress = (
  schedule: OpeningsSchedule,
  fallback: OpeningsLocationFallback,
) => parseAddressParts(schedule.address, fallback);

/** Today's date (`YYYY-MM-DD`) in Los Angeles, where every location is. */
const losAngelesDate = (now: Date) =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);

/** The registry description, led by a notice while it is still current. */
export const resolveLocationDescription = (
  fallback: Pick<OpeningsLocationFallback, "description" | "notice">,
  now: Date = new Date(),
): string | undefined => {
  const notice =
    fallback.notice && losAngelesDate(now) <= fallback.notice.until
      ? fallback.notice.text
      : undefined;
  return [notice, fallback.description].filter(Boolean).join(" ") || undefined;
};

const adaptHours = (hours?: OpeningsScheduleHour[]) =>
  (hours ?? []).map((hour) => [
    String(hour.day),
    hour.isOpen ? (hour.open ?? "") : "",
    hour.isOpen ? (hour.close ?? "") : "",
  ]);

const adaptLegacyScheduleHours = (hours?: OpeningsScheduleHour[]) => {
  const fallbackHours = Array.from({ length: 7 }, () => ["", "", "off"]);

  for (const hour of hours ?? []) {
    fallbackHours[hour.day] = hour.isOpen
      ? [hour.open ?? "", hour.close ?? "", "on"]
      : ["", "", "off"];
  }

  return fallbackHours;
};

/**
 * Unknown enrichment hours must not mean "closed". These rows only feed the
 * profile date picker's weekday filter, so failing open preserves every day
 * until the independent availability request supplies the real answer.
 */
const adaptOption = (option: OpeningsServiceOption): ServiceOption => ({
  id: option.id,
  referenceId: option.referenceId ?? undefined,
  title: option.title,
  description: option.description ?? undefined,
  price: option.price,
  duration: option.duration,
});

// Mirror the API's payment resolution (apps/api/.../helpers.ts
// `resolveServicePaymentSettings`): a team-member's clone service carries a
// `referenceId` pointing to the business-level (parent) service, and the
// parent is authoritative for the payment requirement. We resolve the same
// way here so the booking modal knows a deposit/full charge is required and
// collects a card up front, instead of letting the server reject the booking
// with "A saved payment method is required for these services".
const normalizePaymentRequirement = (
  paymentRequirement: OpeningsService["paymentRequirement"],
  hasPayAdvance: boolean | undefined,
): Service["paymentRequirement"] => {
  if (paymentRequirement === "FULL" || paymentRequirement === "DEPOSIT") {
    return paymentRequirement;
  }
  return hasPayAdvance ? "DEPOSIT" : "NONE";
};

/**
 * Openings team member tags as the website shows them. The text is the
 * business's own, shown as written; a blank description is dropped so the
 * review's "Before your visit" block never renders an empty note. An older
 * API without the field yields `[]`.
 */
const adaptMemberTags = (
  tags: OpeningsMemberTag[] | null | undefined,
): StylistTag[] =>
  (tags ?? [])
    .filter((tag) => Boolean(tag?.id && tag.label?.trim()))
    .map((tag) => ({
      id: tag.id,
      label: tag.label.trim(),
      description: tag.description?.trim() || null,
    }));

const adaptService = (
  service: OpeningsService,
  referenceService?: OpeningsService | null,
): Service => {
  const paymentSource = referenceService ?? service;

  return {
    _id: service.id,
    referenceId: service.referenceId ?? undefined,
    title: service.title,
    description: service.description ?? undefined,
    price: service.price,
    category: getServiceCategory(service.title),
    duration: service.duration,
    up: service.up,
    hasPayAdvance: Boolean(paymentSource.hasPayAdvance),
    hasConsultation: service.hasConsultation,
    paymentRequirement: normalizePaymentRequirement(
      paymentSource.paymentRequirement,
      paymentSource.hasPayAdvance,
    ),
    depositAmount: paymentSource.depositAmount ?? service.depositAmount,
    options: service.options?.map(adaptOption),
    // Same rule as the payment requirement: the host service binds a clone.
    // A clone whose host is not in hand keeps its own value, which the
    // members payload already resolved to the host's.
    minBookingNoticeMinutes: resolveEffectiveMinBookingNotice(
      service,
      referenceService,
    ),
  };
};

const adaptMemberDetailsPhoto = (
  photo: OpeningsMemberDetailsPhoto,
  memberName: string,
  index: number,
  salonId?: string,
): UserData["photos"][number] => ({
  _id: photo.url,
  ...photoEngagementMetadata(photo, salonId),
  src: photo.url,
  alt: `${memberName} work photo ${index + 1}`,
  type: photo.type ?? "image",
  variants: photo.thumbnail ? [photo.thumbnail] : [],
});

const adaptMember = (member: OpeningsMember) => {
  const memberId = getOpeningsMemberId(member);

  return {
    _id: memberId,
    userId: memberId,
    name: member.name ?? "Stylist",
    username:
      getEditionUserPathIdFromMemberId(memberId) ?? member.username ?? memberId,
    photoId: member.photo ?? "",
    bio: member.bio ?? "",
  };
};

const adaptMemberUserData = (
  pathId: string,
  member: OpeningsMember,
  schedule: OpeningsSchedule,
  services: OpeningsService[],
  config: OpeningsLocationConfig,
  userPage?: OpeningsUserPage,
  memberDetails?: OpeningsMemberDetails,
): UserData => {
  const memberId = getOpeningsMemberId(member);
  const address = parseScheduleAddress(schedule, config.fallback);
  const memberName = userPage?.name ?? member.name ?? "Stylist";
  const servicesById = new Map(
    services.map((service) => [service.id, service]),
  );

  return {
    _id: memberId,
    name: memberName,
    userPathId: pathId,
    phone: member.phoneNumber ?? userPage?.phone ?? config.fallback.phone,
    instagramUsername:
      userPage?.instagramHandle?.trim().replace(/^@+/, "") || undefined,
    photo:
      member.photo ??
      userPage?.photo ??
      memberDetails?.profilePhoto ??
      undefined,
    bio: member.bio ?? userPage?.bio ?? memberDetails?.bio ?? undefined,
    schedules: [
      {
        _id: schedule.id,
        locationId: schedule.id,
        locationPathId: config.pathId,
        locationName: schedule.title || config.fallback.name,
        locationAddress: `${address.address}, ${address.city}, ${address.state} ${address.zipcode}`,
        locationImage: schedule.images?.[0],
        hours: adaptLegacyScheduleHours(schedule.hours),
        timezone: schedule.timezone ?? null,
      },
    ],
    services: services
      .filter((service) => service.teamMemberId === memberId)
      .map((service) =>
        adaptService(
          service,
          service.referenceId ? servicesById.get(service.referenceId) : null,
        ),
      ),
    vacations: [],
    photos: userPage
      ? adaptMemberPortfolio(userPage.photos, memberName, schedule.salonId)
      : (memberDetails?.photos ?? []).map((photo, index) =>
          adaptMemberDetailsPhoto(photo, memberName, index, schedule.salonId),
        ),
    discounts: [],
    tags: adaptMemberTags(userPage?.tags ?? memberDetails?.tags),
  };
};

/** Build profile location rows only from roster-verified live routes. */
const buildMemberSchedules = (
  routes: LiveLocationRoute[],
): UserData["schedules"] =>
  routes.map(({ config, detail, scheduleId }) => {
    const schedule = detail.schedule;
    const address = parseScheduleAddress(schedule, config.fallback);
    return {
      _id: scheduleId,
      locationId: scheduleId,
      locationPathId: config.pathId,
      locationName: schedule.title || config.fallback.name,
      locationAddress: `${address.address}, ${address.city}, ${address.state} ${address.zipcode}`,
      locationImage: schedule.images?.[0],
      hours: adaptLegacyScheduleHours(schedule.hours),
      timezone: schedule.timezone ?? null,
    };
  });

const getBookableServices = (services: OpeningsService[]) =>
  services.filter((service) => !service.teamMemberId && !service.referenceId);

const getScheduleImages = (
  schedule: OpeningsSchedule,
  location?: OpeningsSchedule,
) => {
  if (schedule.images?.length) return schedule.images;
  if (location?.images?.length) return location.images;
  return [] as string[];
};

const getScheduleParking = (
  schedule: OpeningsSchedule,
  location: OpeningsSchedule | undefined,
  fallbackMap: string,
) => {
  const parking = schedule.parking ?? location?.parking;
  if (!parking?.description) return undefined;

  return {
    description: parking.description,
    map: parking.link ?? fallbackMap,
  };
};

/** A selected service as carried through the booking form. */
type SelectedService = Service & { option?: ServiceOption };

const unique = <T>(items: T[]): T | undefined =>
  items.length === 1 ? items[0] : undefined;

const findSelectedService = (
  services: OpeningsService[],
  selected: Pick<Service, "_id" | "referenceId" | "title" | "category">,
  memberId?: string,
): OpeningsService | undefined => {
  const candidates = memberId
    ? services.filter((item) => item.teamMemberId === memberId)
    : services;
  const selectedIds = new Set(
    [selected._id, selected.referenceId].filter(Boolean) as string[],
  );
  const exact = candidates.filter(
    (item) =>
      selectedIds.has(item.id) ||
      (item.referenceId ? selectedIds.has(item.referenceId) : false),
  );
  if (exact.length === 1) return exact[0];
  if (exact.length > 1) return undefined;

  const byTitle = candidates.filter(
    (item) => normalizeTitle(item.title) === normalizeTitle(selected.title),
  );
  if (byTitle.length === 1) return byTitle[0];
  if (byTitle.length > 1 || selected.category <= 0) return undefined;

  return unique(
    candidates.filter(
      (item) => getServiceCategory(item.title) === selected.category,
    ),
  );
};

const findSelectedOption = (
  service: OpeningsService,
  selected: Pick<ServiceOption, "id" | "referenceId" | "title">,
): OpeningsServiceOption | undefined => {
  const options = service.options ?? [];
  const selectedIds = new Set(
    [selected.id, selected.referenceId].filter(Boolean) as string[],
  );
  const exact = options.filter(
    (item) =>
      selectedIds.has(item.id) ||
      (item.referenceId ? selectedIds.has(item.referenceId) : false),
  );
  if (exact.length === 1) return exact[0];
  if (exact.length > 1) return undefined;

  return unique(
    options.filter(
      (item) => normalizeTitle(item.title) === normalizeTitle(selected.title),
    ),
  );
};

const getServiceSelection = (
  services: OpeningsService[],
  selected: SelectedService[],
) => {
  const bookableServices = getBookableServices(services);
  const resolved = selected.map((selection) => {
    const selectedIds = new Set(
      [selection._id, selection.referenceId].filter(Boolean) as string[],
    );
    const service = unique(
      bookableServices.filter(
        (item) =>
          selectedIds.has(item.id) ||
          (item.referenceId ? selectedIds.has(item.referenceId) : false),
      ),
    );
    if (!service) return undefined;
    const option = selection.option
      ? unique(
          (service.options ?? []).filter(
            (item) =>
              item.id === selection.option?.id ||
              item.referenceId === selection.option?.id ||
              (selection.option?.referenceId != null &&
                (item.id === selection.option.referenceId ||
                  item.referenceId === selection.option.referenceId)),
          ),
        )
      : undefined;
    if (selection.option && !option) return undefined;
    return {
      serviceId: service.id,
      ...(option ? { optionId: option.id } : {}),
    };
  });

  // Partial service lists produce availability for an appointment that cannot
  // actually be booked. Fail the entire selection closed instead.
  return resolved.every(Boolean)
    ? (resolved as { serviceId: string; optionId?: string }[])
    : [];
};

const adaptUserOpenings = (
  item: OpeningsNextAvailabilityItem,
  route: LiveLocationRoute,
  memberId: string,
  services?: SelectedService[],
): UserOpeningsData & { date?: string } => {
  const { config } = route;
  const { branding } = BUSINESSES[config.business];
  const badgeLabel =
    branding.presentsAs === "own-brand" ? branding.badgeLabel : undefined;

  return {
    ...(item.date ? { date: item.date } : {}),
    openings: item.openings,
    ...(services ? { services } : {}),
    schedule: {
      _id: item.schedule.id,
      locationId: route.scheduleId,
      locationName: item.schedule.title || config.fallback.name,
      memberId,
      ...(badgeLabel ? { badgeLabel } : {}),
      hours: adaptHours(item.schedule.hours),
    },
  };
};

const getOpeningsAppointmentServices = async (
  scheduleId: string,
  args: {
    userId: string;
    services: ICreateAppointmentArgs["services"];
  },
) => {
  const detail = await OpeningsJson.getScheduleDetail(scheduleId);

  return args.services.map((selectedService) => {
    // Write payloads carry stable target IDs after availability resolution.
    // The category fallback is intentionally only used when it is non-zero
    // and unique; category 0 represents many unrelated Studio services.
    const service = findSelectedService(
      detail.services,
      {
        ...selectedService,
        title: "",
        referenceId: undefined,
      },
      args.userId,
    );

    if (!service) {
      throw new Error(
        "Selected service is not available with this stylist. Please choose another stylist or service.",
      );
    }

    const option = selectedService.optionId
      ? unique(
          (service.options ?? []).filter(
            (item) =>
              item.id === selectedService.optionId ||
              item.referenceId === selectedService.optionId,
          ),
        )
      : undefined;

    if (selectedService.optionId && !option) {
      throw new Error(
        "Selected service option is not available with this stylist. Please choose another option.",
      );
    }

    return {
      id: service.id,
      ...(option ? { optionId: option.id } : {}),
    };
  });
};

/**
 * A deterministic, customer-facing pricing rejection: the current selection
 * cannot be priced at the booked location, and retrying will not change that.
 * The MESSAGE is written for customers and safe to render verbatim; anything
 * else thrown from the pricing path (network failures, upstream error bodies)
 * is not, and the UI must fall back to generic copy.
 */
export class ServicePricingSelectionError extends Error {}

/**
 * The selected location service/option no longer resolves against the latest
 * host catalog. Callers can distinguish this deterministic selection problem
 * from transient availability failures without parsing an error message.
 */
export class LocationServiceSelectionError extends Error {
  constructor() {
    super(
      "The selected service or option is no longer available at this location.",
    );
    this.name = "LocationServiceSelectionError";
  }
}

/**
 * Re-price a chosen option against the matched target-business service's own
 * options, mirroring `getOpeningsAppointmentServices`'s option resolution
 * (stable id/reference first, then one unambiguous normalized title).
 */
const repriceOption = (
  match: OpeningsService,
  option: ServiceOption,
): ServiceOption => {
  const targetOption = findSelectedOption(match, option);
  // A matched option without a numeric price is payload drift (the wire type
  // requires one). Keeping the canonical business's number would be exactly
  // the stale price basis this gate exists to prevent — fail closed instead.
  if (!targetOption || typeof targetOption.price !== "number") {
    throw new ServicePricingSelectionError(
      "Selected service option is unavailable at this location.",
    );
  }

  return {
    ...adaptOption(targetOption),
    duration: targetOption.duration ?? option.duration,
  };
};

/**
 * Re-price selected services against the schedule/member they will actually
 * be booked with. A stylist's menu comes from their canonical business, but a
 * cross-business slot books elsewhere, where the same service has a different
 * id and price. Resolution uses stable IDs within a business and one
 * unambiguous normalized title across businesses, with only a unique nonzero
 * legacy-category fallback. Missing/ambiguous services, options, or schedule
 * details reject so callers cannot display a stale price/payment basis.
 *
 * A chosen option is re-priced too (`repriceOption`): `formatServicePrice`
 * prefers an option's price over the base service's, so leaving it untouched
 * would still show the canonical business's option price on a cross-business
 * booking with an option selected.
 *
 * The payment BASIS travels with the price. `paymentRequirement`,
 * `depositAmount` and `hasPayAdvance` decide whether the modal collects a
 * card at all and how much it says it will charge, and they are per-business
 * settings just like price is: canonical `NONE` + booked `DEPOSIT` means the
 * card step never renders and the server rejects the booking at create, while
 * canonical `DEPOSIT $A` + booked `DEPOSIT $B` shows "Charge $A" while the
 * API charges $B. They are resolved exactly the way `adaptService` resolves
 * them — through the clone's `referenceId` to the business-level parent
 * service, which is authoritative (mirrors the API's own
 * `resolveServicePaymentSettings`).
 */
export const getOpeningsBackedServicePricing = async (
  scheduleId: string,
  memberId: string,
  services: SelectedService[],
): Promise<SelectedService[]> => {
  const detail = await OpeningsJson.getScheduleDetail(scheduleId);

  const servicesById = new Map(detail.services.map((item) => [item.id, item]));

  return services.map((selected) => {
    const match = findSelectedService(detail.services, selected, memberId);
    // A match without a numeric price is payload drift (the wire type
    // requires one): the target business never stated a price, so reporting
    // "ready" with the canonical number would be the stale basis this
    // function's contract promises to reject.
    if (!match || typeof match.price !== "number") {
      throw new ServicePricingSelectionError(
        "Selected service is unavailable at this location.",
      );
    }

    const paymentSource =
      (match.referenceId ? servicesById.get(match.referenceId) : null) ?? match;

    return {
      ...selected,
      price: match.price,
      duration: match.durationMinutes ?? match.duration ?? selected.duration,
      hasPayAdvance: Boolean(paymentSource.hasPayAdvance),
      paymentRequirement: normalizePaymentRequirement(
        paymentSource.paymentRequirement,
        paymentSource.hasPayAdvance,
      ),
      depositAmount: paymentSource.depositAmount ?? match.depositAmount ?? null,
      option: selected.option
        ? repriceOption(match, selected.option)
        : selected.option,
    };
  });
};

const adaptOpeningsMember = (
  member: OpeningsMemberOpenings,
  config: OpeningsLocationConfig,
  locationId: string,
  servicesById?: ReadonlyMap<string, OpeningsService>,
): LocationOpeningsData => {
  const memberId = member.teamMemberId ?? member.userId ?? member.username;

  return {
    userId: memberId,
    username: getEditionUserPathIdFromMemberId(memberId) ?? member.username,
    name: member.name,
    photo: member.photo ?? "",
    phone: member.phoneNumber ?? "",
    video: member.video,
    services: member.services.map((service) =>
      adaptService(
        service,
        service.referenceId ? servicesById?.get(service.referenceId) : null,
      ),
    ),
    schedule: {
      _id: member.schedule.id,
      locationId,
      title: member.schedule.title ?? config.fallback.name,
      hours: adaptHours(member.schedule.hours),
    },
    openings: member.openings,
    dayStatus: member.dayStatus ?? "workday",
    vacationRange: member.vacationRange ?? null,
    tags: adaptMemberTags(member.tags),
  };
};

export const isOpeningsBackedLocation = (locationId: string) =>
  Boolean(getConfigByLocationId(locationId));

export const isOpeningsBackedMember = (memberId: string | null | undefined) =>
  Boolean(getBusinessForMemberId(memberId));

const getConfigForAppointment = (appointment?: {
  userId?: string | null;
  locationId?: string | null;
  locationName?: string | null;
  scheduleId?: string | null;
}): OpeningsLocationConfig | undefined => {
  if (!appointment) return undefined;
  const memberBusiness = getBusinessForMemberId(appointment.userId);
  const direct =
    getConfigByLocationId(appointment.locationId) ??
    getConfigByLocationId(appointment.scheduleId) ??
    getConfigByLocationName(appointment.locationName);
  if (direct) return direct;

  // A member identity safely narrows the tenant but not a location. It can
  // identify the location only when that business exposes exactly one route.
  const businessRoutes = memberBusiness
    ? OPENINGS_LOCATIONS.filter((config) => config.business === memberBusiness)
    : [];
  return businessRoutes.length === 1 ? businessRoutes[0] : undefined;
};

export const isOpeningsBackedAppointment = (appointment?: {
  userId?: string | null;
  locationId?: string | null;
  locationName?: string | null;
  scheduleId?: string | null;
}) =>
  Boolean(
    getBusinessForMemberId(appointment?.userId) ||
    getConfigForAppointment(appointment),
  );

/**
 * What a booking scene knows about its own appointment — the subset of
 * `AppointmentForm` the façade already carries, and exactly what
 * `getConfigForAppointment` consumes.
 */
export type OpeningsAppointmentContext = {
  userId?: string | null;
  locationId?: string | null;
  scheduleId?: string | null;
  locationName?: string | null;
};

/**
 * Every business that a resolvable identifier on this appointment points to,
 * deduped. Checks the member, the locationId, the scheduleId, and the
 * locationName independently (NOT as a first-match chain) — `args.scheduleId`
 * is not reliably a registry schedule id (see `requireBusinessForAppointment`
 * below), so a real booking often carries one identifier that resolves and
 * one that legitimately does not, and both must be checked to catch a genuine
 * disagreement between the two that DO resolve.
 *
 * An identifier that resolves nothing contributes nothing — a member's own
 * schedule id simply will not match the registry, and a member absent from
 * `@/consts/editionStudio` will not either. That is expected, not an error.
 */
const resolveAppointmentBusinesses = (
  appointment: OpeningsAppointmentContext,
): EditionBusinessKey[] => {
  const businesses = [
    getBusinessForMemberId(appointment.userId),
    getConfigByLocationId(appointment.locationId)?.business,
    getConfigByLocationId(appointment.scheduleId)?.business,
    getConfigByLocationName(appointment.locationName)?.business,
  ].filter((business): business is EditionBusinessKey => Boolean(business));

  return Array.from(new Set(businesses));
};

/**
 * Resolve the Openings business a booking belongs to, fail-closed.
 *
 * Checks every identifier independently via `resolveAppointmentBusinesses`
 * rather than taking `getConfigForAppointment`'s first match, because
 * `args.scheduleId` is NOT reliably a registry schedule id: on the location
 * page it is the member's OWN schedule id (`adaptOpeningsMember` puts the
 * registry id in `locationId` instead). A first-match chain would silently
 * pick whichever identifier happened to resolve first even when a SECOND
 * identifier resolves to a DIFFERENT business — exactly the case where a
 * member from business A gets booked against a schedule/location in business
 * B. Two or more distinct businesses among the resolved identifiers means the
 * booking spans two businesses, so this throws rather than picking one.
 *
 * Throwing on zero matches is also deliberate — a wrong businessId finds no
 * Customer row (Customer is unique on (salonId, phoneNumber)), so the deposit
 * gate would silently return "no gate" instead of erroring.
 *
 * The member is one link in the chain but never the only one: members come
 * from the Openings API, not this registry, so a stylist who is bookable in
 * Openings but missing from `@/consts/editionStudio` still resolves through
 * `locationId` / `scheduleId` rather than hard-failing at the deposit step.
 */
const requireBusinessForAppointment = (
  appointment: OpeningsAppointmentContext,
): EditionBusinessKey => {
  const businesses = resolveAppointmentBusinesses(appointment);

  if (businesses.length > 1) {
    throw new Error(
      `This appointment's identifiers disagree about which business it belongs to (member "${appointment.userId ?? ""}", location "${appointment.locationId ?? ""}", schedule "${appointment.scheduleId ?? ""}" resolve to businesses: ${businesses.join(", ")}).`,
    );
  }

  const [business] = businesses;
  if (!business) {
    throw new Error(
      `Cannot resolve an Openings business for this appointment (member "${appointment.userId ?? ""}", schedule "${appointment.scheduleId ?? ""}").`,
    );
  }
  return business;
};

/**
 * The business a booking will land in, or `undefined` when the appointment's
 * identifiers resolve to none — or to more than one, which
 * `requireBusinessForAppointment` refuses outright on the write path.
 *
 * Shares `resolveAppointmentBusinesses` with that guard so a read-side
 * decision can never be made against a different notion of "the booked
 * business" than the one the write path enforces. Read-side callers must
 * treat `undefined` as "unknown", never as a default.
 */
export const resolveBusinessForAppointment = (
  appointment: OpeningsAppointmentContext,
): EditionBusinessKey | undefined => {
  const businesses = resolveAppointmentBusinesses(appointment);
  return businesses.length === 1 ? businesses[0] : undefined;
};

/**
 * Best-effort sibling of `requireBusinessForAppointment` for the phone-lookup
 * and OTP steps, which legitimately run before a stylist or location has been
 * chosen. Same chain; falls back to the default ONLY when every link misses —
 * the genuinely context-free first lookup (ruling R16). It must not throw:
 * that would break the opening step of the booking flow.
 */
const resolveBusinessForContext = (
  appointment: OpeningsAppointmentContext | undefined,
): EditionBusinessKey => {
  if (!appointment) return DEFAULT_EDITION_BUSINESS;
  const businesses = resolveAppointmentBusinesses(appointment);
  return businesses.length === 1 ? businesses[0] : DEFAULT_EDITION_BUSINESS;
};

/**
 * Total number of haircuts served for a business (completed / past
 * appointments, excluding cancellations and no-shows). Powers the landing
 * page "haircuts served" counter. Returns 0 if the Openings API is
 * unavailable so the marketing section can degrade gracefully.
 */
export const getOpeningsBackedServedCount = async (
  business?: EditionBusinessKey,
): Promise<number> => {
  const resolved = await resolveOpeningsBusiness(business);
  const { count } = await OpeningsJson.getAppointmentCount(resolved.id);
  return count;
};

const adaptPhoto = (photo: OpeningsPhotoItem, salonId: string): PhotosResponse => {
  const memberId = photo.user?.id ?? "";
  // Openings usernames may be login emails or internal member IDs. Only a
  // registry-backed Edition path is safe to expose as a public profile URL.
  const username = getEditionUserPathIdFromMemberId(memberId) ?? "";

  return {
    _id: photo.id,
    ...photoEngagementMetadata(photo, salonId),
    src: photo.src,
    alt: photo.alt,
    type: photo.type,
    user: {
      _id: memberId,
      username,
      name: photo.user?.name ?? "",
      photo: photo.user?.photo ?? "",
    },
    variants: [photo.src],
    ...(typeof photo.likeCount === "number"
      ? { likeCount: photo.likeCount }
      : {}),
    ...(typeof photo.liked === "boolean" ? { liked: photo.liked } : {}),
    ...(photo.service
      ? { service: { category: 0, title: photo.service.title } }
      : {}),
  };
};

export const getOpeningsBackedPhotos = async (params: {
  limit: number;
  cursor?: string;
  category: string;
  business?: EditionBusinessKey;
  deviceId?: string;
}): Promise<PagedResult<PhotosResponse>> => {
  const business = await resolveOpeningsBusiness(params.business);
  const payload = await OpeningsJson.getPhotos({
    businessId: business.id,
    limit: params.limit,
    cursor: params.cursor,
    category: params.category,
    deviceId: params.deviceId,
  });

  return {
    items: payload.items.map((photo) => adaptPhoto(photo, business.id)),
    nextCursor: payload.nextCursor != null ? String(payload.nextCursor) : null,
    total: payload.total,
  };
};

export const getOpeningsBackedLikedPhotoIds = async (
  deviceId: string,
  business?: EditionBusinessKey,
): Promise<string[]> => {
  const resolved = await resolveOpeningsBusiness(business);
  try {
    const payload = await OpeningsJson.getLikedPhotoIds({
      businessId: resolved.id,
      deviceId,
    });
    return payload.likedPhotoIds ?? [];
  } catch {
    return [];
  }
};

export const getOpeningsBackedPhotoCategories = async (
  business?: EditionBusinessKey,
): Promise<{ key: string; label: string }[]> => {
  const resolved = await resolveOpeningsBusiness(business);
  let categories: { id: string; name: string; sortOrder?: number }[] = [];
  try {
    const payload = await OpeningsJson.getPhotoCategories(resolved.id);
    categories = [...payload.items].sort(
      (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
    );
  } catch {
    categories = [];
  }

  return [
    { key: "all", label: "All" },
    ...categories.map((category) => ({
      key: category.id,
      label: category.name,
    })),
  ];
};

type TeamLocation = {
  locationId: string;
  locationName: string;
  users: { name: string; image: string; userId: string; username: string }[];
};

/**
 * List a business's bookable locations with the stylists assigned to each,
 * sourced from the Openings API. Only stylists that resolve to an Edition
 * profile path (`getEditionUserPathIdFromMemberId`) are included so the Team
 * page links keep pointing at working `/stylists/:pathId` pages.
 */
export const getOpeningsBackedTeam = async (
  business?: EditionBusinessKey,
): Promise<TeamLocation[]> => {
  const directory = await getBusinessRouteDetails(
    business ?? DEFAULT_EDITION_BUSINESS,
  );

  const locations: TeamLocation[] = [];

  for (const { scheduleId, detail } of directory.routes) {
    const { schedule } = detail;

    const users: TeamLocation["users"] = [];
    for (const member of detail.members) {
      const memberId = getOpeningsMemberId(member);
      const username = getEditionUserPathIdFromMemberId(memberId);
      if (!username) continue;
      users.push({
        name: member.name ?? "Stylist",
        image: member.photo ?? "",
        userId: memberId,
        username,
      });
    }

    if (users.length === 0) continue;

    locations.push({
      locationId: scheduleId,
      locationName: schedule.title || "",
      users,
    });
  }

  return locations;
};

/** The discovery page preserves the public website roster, independent of booking. */
export const getOpeningsBackedStylistDirectory = async (
  loadProfile = OpeningsJson.getUserPage,
): Promise<StylistDirectory> => {
  const directories = await Promise.all(
    BUSINESS_KEYS.map(async (business) => {
      const directory = await getBusinessRouteDetails(business).catch(() => ({
        businessId: undefined, routes: [], hadFailure: true,
      }));
      const roster = directory.businessId
        ? await OpeningsJson.getTeamPage(directory.businessId)
            .then(({ members }) => ({ members, hadFailure: false }))
            .catch(() => ({ members: [], hadFailure: true }))
        : { members: [], hadFailure: true };
      return { business, directory, roster };
    }),
  );
  const locations: StylistDirectory["locations"] = [];
  const people = new Map<string, StylistCardData>();
  const profiles = new Map<string, { memberId: string; businessId: string }>();

  // Eddie's roster first, then additional Studio people in Studio's order.
  // Shared people keep one card and their canonical business's profile/tenure.
  for (const { business, directory, roster } of directories) {
    for (const member of roster.members) {
      const username = getEditionUserPathIdFromMemberId(member.id);
      // Public profile routes still require the Edition identity registry.
      if (!username) continue;
      const canonicalBusiness = getCanonicalBusinessForStylist(username);
      const stylist: StylistCardData = people.get(username) ?? {
        memberId: getStylistMemberships(username).find(
          (membership) => membership.business === canonicalBusiness,
        )?.memberId ?? member.id,
        username,
        name: member.name || "Stylist",
        avatar: member.photo || undefined,
        bio: member.bio || undefined,
        photos: [], locationIds: [], servicesByLocation: {},
      };
      if (business === canonicalBusiness && directory.businessId) {
        stylist.memberId = member.id;
        stylist.name = member.name || stylist.name;
        stylist.avatar = member.photo || undefined;
        stylist.bio = member.bio || undefined;
        stylist.workingSince = member.workingSince;
        profiles.set(username, { memberId: member.id, businessId: directory.businessId });
      }
      people.set(username, stylist);
    }

    for (const { config, scheduleId, detail } of directory.routes) {
      const { schedule } = detail;
      let hasStylists = false;
      for (const member of roster.members) {
        if (!member.schedules.some((item) => item.id === scheduleId)) continue;
        const username = getEditionUserPathIdFromMemberId(member.id);
        const stylist = username ? people.get(username) : undefined;
        if (!stylist) continue;
        hasStylists = true;
        if (!stylist.locationIds.includes(scheduleId)) stylist.locationIds.push(scheduleId);
        const categories = detail.services
          .filter((service) => service.teamMemberId === member.id)
          .map((service) => getServiceCategory(service.title));
        stylist.servicesByLocation[scheduleId] = STYLIST_SERVICES
          .filter((service) => service.categories.some((category) => categories.includes(category)))
          .map((service) => service.id);
      }
      if (hasStylists) locations.push({
        id: scheduleId, pathId: config.pathId,
        name: schedule.title || config.fallback.name,
        address: schedule.address || [config.fallback.address, config.fallback.city].join(", "),
        lat: schedule.lat ?? config.fallback.lat,
        lng: schedule.lng ?? config.fallback.lng,
      });
    }
  }

  const stylists = [...people.values()];
  // Bound cold-cache fanout. Public profile reads already use the 5-minute
  // Next data cache; one unavailable enrichment must not hide the roster.
  let next = 0;
  let profileRateLimited = false;
  await Promise.all(Array.from({ length: Math.min(6, stylists.length) }, async () => {
    while (next < stylists.length) {
      const stylist = stylists[next++];
      const identity = profiles.get(stylist.username);
      if (!identity || profileRateLimited) continue;
      const profile = await loadProfile({
        identifier: identity.memberId, businessId: identity.businessId,
      }).catch((error: unknown) => {
        // Don't spend the rest of the quota on doomed profile reads. The
        // directory can still get work previews from the bulk photo feed.
        if (error && typeof error === "object" && "status" in error && error.status === 429) {
          profileRateLimited = true;
        }
        return undefined;
      });
      if (!profile) continue;
      stylist.avatar = profile.photo || stylist.avatar;
      stylist.bio = profile.bio || stylist.bio;
      const handle = profile.instagramHandle?.trim().replace(/^@+/, "");
      stylist.instagram = handle && /^[a-zA-Z0-9._]+$/.test(handle) ? handle : undefined;
      stylist.portfolioLoaded = true;
      stylist.photos = (profile.photos ?? [])
        .filter((photo) => photo.src)
        .filter((photo, index, photos) => photos.findIndex((item) => item.src === photo.src) === index)
        .map((photo, index) => ({ ...photoEngagementMetadata(photo, identity.businessId), src: photo.src, alt: photo.alt || `Work by ${stylist.name}, photo ${index + 1}`, type: photo.type ?? "image" }));
    }
  }));

  return { stylists, locations, partial: directories.some(({ directory, roster }) => directory.hadFailure || roster.hadFailure) };
};

/**
 * List the public stylists who currently carry a named service, consolidating
 * multi-location stylists into one compact row for service landing pages.
 */
export const getOpeningsBackedServiceStylists = async (
  serviceTitle: string,
  business?: EditionBusinessKey,
): Promise<ServiceStylistData[]> => {
  const directory = await getBusinessRouteDetails(
    business ?? DEFAULT_EDITION_BUSINESS,
  );
  const normalizedServiceTitle = normalizeTitle(serviceTitle);
  const stylistsByUsername = new Map<string, ServiceStylistData>();

  for (const { detail } of directory.routes) {
    const memberIds = new Set(
      detail.services
        .filter(
          (service) =>
            Boolean(service.teamMemberId) &&
            normalizeTitle(service.title) === normalizedServiceTitle,
        )
        .map((service) => service.teamMemberId as string),
    );

    for (const member of detail.members) {
      const memberId = getOpeningsMemberId(member);
      if (!memberIds.has(memberId)) continue;

      const username = getEditionUserPathIdFromMemberId(memberId);
      if (!username) continue;

      const locationName = detail.schedule.title || "";
      const existing = stylistsByUsername.get(username);
      if (existing) {
        if (locationName && !existing.locations.includes(locationName)) {
          existing.locations.push(locationName);
        }
        continue;
      }

      stylistsByUsername.set(username, {
        name: member.name ?? "Stylist",
        image: member.photo ?? "",
        username,
        locations: locationName ? [locationName] : [],
      });
    }
  }

  return [...stylistsByUsername.values()]
    .map((stylist) => ({
      ...stylist,
      locations: [...stylist.locations].sort((a, b) => a.localeCompare(b)),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

export type { BusinessService } from "./types";

const normalizeServiceKey = normalizeTitle;

/**
 * Aggregate a business's bookable services into a deduped catalog with the
 * starting (minimum) price per service, sourced live from the Openings API.
 * Services are grouped by normalized title across every schedule so the
 * Services page shows one "from $X" entry per offering.
 */
export const getOpeningsBackedServices = async (
  business?: EditionBusinessKey,
): Promise<BusinessService[]> => {
  const resolved = await resolveOpeningsBusiness(business);
  const [{ items }, catalog, categories] = await Promise.all([
    OpeningsJson.getBusinessSchedules(resolved.id),
    OpeningsJson.getBusinessServices(resolved.id).catch(() => ({
      services: [],
    })),
    OpeningsJson.getServiceCategories(resolved.id).catch(() => ({ items: [] })),
  ]);

  const categoriesById = new Map(
    categories.items.map((category) => [category.id, category]),
  );

  const hostServicesById = new Map(
    catalog.services
      .filter((service) => !service.referenceId)
      .map((service) => [service.id, service]),
  );

  const details = await Promise.all(
    items.map((schedule) =>
      OpeningsJson.getScheduleDetail(schedule.id).catch(() => null),
    ),
  );

  const grouped = new Map<string, BusinessService>();

  const addService = (service: OpeningsService) => {
    const hostService = service.referenceId
      ? hostServicesById.get(service.referenceId)
      : hostServicesById.get(service.id) ?? service;
    const title = hostService?.title ?? service.title;
    const key = normalizeServiceKey(title);
    const description = hostService?.description?.trim() || undefined;
    const categoryId = hostService ? hostService.categoryId : service.categoryId;
    const category = categoryId ? categoriesById.get(categoryId) : undefined;
    const existing = grouped.get(key);

    if (!existing) {
      grouped.set(key, {
        title,
        price: service.price,
        description,
        ...(category ? { category } : {}),
        ...((hostService ?? service).hasConsultation ? { hasConsultation: true } : {}),
      });
      return;
    }

    if (service.price < existing.price) existing.price = service.price;
    if (!existing.description && description) {
      existing.description = description;
    }
    if (!existing.category && category) existing.category = category;
  };

  // Each member gets a per-member clone of every service (their own price +
  // free-text description, which often contains personal price lists). The
  // host service has no `referenceId` and is the canonical entry with the clean
  // title, base price, and marketing description, so prefer it. Clones only
  // contribute offerings that have no host-level counterpart.
  const hostServices: OpeningsService[] = [];
  const memberServices: OpeningsService[] = [];

  for (const detail of details) {
    if (!detail) continue;
    for (const service of detail.services) {
      if (typeof service.price !== "number" || service.price <= 0) continue;
      (service.referenceId ? memberServices : hostServices).push(service);
    }
  }

  for (const service of hostServices) addService(service);
  for (const service of memberServices) {
    if (grouped.has(normalizeServiceKey(service.title))) continue;
    addService(service);
  }

  // Preserve the Openings catalog order (the Map keeps first-seen insertion
  // order, which mirrors the API's service ordering).
  return [...grouped.values()];
};

/**
 * Build a Google Maps URL from the schedule's address (Openings does not
 * provide a map link). Falls back to the registry value.
 */
const buildScheduleMapUrl = (
  schedule: OpeningsSchedule,
  fallbackMap: string,
): string =>
  schedule.address
    ? `https://maps.google.com/?q=${encodeURIComponent(schedule.address)}`
    : fallbackMap;

export const getOpeningsBackedLocation = async (
  locationId: string,
): Promise<LocationData> => {
  const config = getConfigByLocationId(locationId);
  if (!config) {
    // Unknown / decommissioned location (e.g. stale URLs hit by crawlers or
    // prefetch bursts): render the 404 page instead of crashing the
    // serverless function with a 500. Matches getOpeningsBackedUser.
    notFound();
  }

  const resolution = await getLiveLocationRoute(config);
  if (!resolution.route) {
    if (resolution.hadFailure) {
      throw new Error(`${config.fallback.name} is temporarily unavailable.`);
    }
    notFound();
  }
  const detail = resolution.route.detail;
  const schedule = detail.schedule;
  const address = parseScheduleAddress(schedule, config.fallback);
  const services = getBookableServices(detail.services).map((service) =>
    adaptService(service),
  );
  const images = getScheduleImages(schedule, detail.location);
  const map = buildScheduleMapUrl(schedule, config.fallback.map);
  const parking = getScheduleParking(schedule, detail.location, map);

  return {
    location: {
      _id: schedule.id,
      pathId: config.pathId,
      name: schedule.title || config.fallback.name,
      ...address,
      lat: schedule.lat ?? config.fallback.lat,
      lng: schedule.lng ?? config.fallback.lng,
      googlePlaceId: schedule.googlePlaceId?.trim() || null,
      images,
      banners: images,
      phone: config.fallback.phone,
      map,
      parking,
      // OpeningsSchedule has no description field today; when the API grows
      // one, prefer it here the same way every other field above prefers
      // live data over the registry fallback.
      description: resolveLocationDescription(config.fallback),
      services: services.map((service) => service.category),
      timezone: schedule.timezone ?? null,
    },
    members: detail.members.map(adaptMember),
    services,
  };
};

const EMPTY_LOCATION_FALLBACK: OpeningsLocationFallback = {
  name: "",
  address: "",
  city: "",
  state: "",
  zipcode: "",
  lat: 0,
  lng: 0,
  phone: "",
  map: "",
};

/**
 * Every bookable location across every registered business, adapted to the
 * Eddie `Location` shape. `pathId` and `phone` come from the registry; all
 * other fields are API-driven. A schedule the registry has no entry for is
 * skipped — it has no route to link to, so a broken card is worse than no
 * card. Each business is wrapped in its own try/catch so one business being
 * unavailable cannot blank the whole browse page.
 */
export const getOpeningsBackedLocations = async (): Promise<Location[]> => {
  const perBusiness = await Promise.all(
    BUSINESS_KEYS.map(async (businessKey) => {
      try {
        const directory = await getBusinessRouteDirectory(businessKey);

        return directory.routes.map(({ schedule, config, scheduleId }) => {
          const address = parseScheduleAddress(schedule, config.fallback);
          const images = getScheduleImages(schedule);
          const map = buildScheduleMapUrl(schedule, config.fallback.map);

          return {
            _id: scheduleId,
            pathId: config.pathId,
            name: schedule.title || config.fallback.name,
            ...address,
            lat: schedule.lat ?? config.fallback.lat,
            lng: schedule.lng ?? config.fallback.lng,
            googlePlaceId: schedule.googlePlaceId?.trim() || null,
            images,
            banners: images,
            bannerImage: schedule.bannerImage,
            cardImage: schedule.cardImage,
            phone: config.fallback.phone || undefined,
            map,
            parking: getScheduleParking(schedule, undefined, map),
            services: [],
          };
        });
      } catch (e) {
        // One business being unavailable must not blank the whole browse page.
        console.error(e);
        return [] as Location[];
      }
    }),
  );

  return perBusiness.flat();
};

/**
 * The stylist profile behind `/stylists/<pathId>`.
 *
 * The CANONICAL business decides whose bio, photos and service menu own the
 * page. Location rows are roster-verified live routes, never static staffing.
 */
export const getOpeningsBackedUser = async (
  pathId: string,
): Promise<UserData> => {
  const canonicalBusiness = getCanonicalBusinessForStylist(pathId);
  const memberships = getStylistMemberships(pathId);
  if (!canonicalBusiness || memberships.length === 0) notFound();

  const liveMemberships = await Promise.all(
    memberships.map(getLiveStylistRoutes),
  );
  const canonical = liveMemberships.find(
    (membership) => membership.business === canonicalBusiness,
  );
  if (!canonical?.businessId || canonical.routes.length === 0) {
    if (canonical?.hadFailure) {
      throw new Error("This stylist's locations are temporarily unavailable.");
    }
    notFound();
  }

  const primaryRoute = canonical.routes[0];
  const { config, detail, memberId } = {
    config: primaryRoute.config,
    detail: primaryRoute.detail,
    memberId: canonical.memberId,
  };
  const member = detail.members.find(
    (item) => getOpeningsMemberId(item) === memberId,
  );
  if (!member) notFound();

  // userPage and memberDetails only enrich the profile (photos, stats, bio,
  // reviews) — adaptMemberUserData renders without them. Swallow failures
  // (notably 429s from the per-IP rate limit shared by Vercel's egress IPs)
  // so a throttled enrichment call degrades the page instead of erroring it.
  const [userPage, memberDetails] = await Promise.all([
    OpeningsJson.getUserPage({
      identifier: memberId,
      businessId: canonical.businessId,
    }).catch(() => undefined),
    OpeningsJson.getMemberDetails(memberId).catch(() => undefined),
  ]);
  // Tags are per membership. A slot on this page can book under another
  // business's membership (its own memberId), whose tags the canonical
  // profile does not carry, so read each live membership's public details
  // (data-cached). A failed read leaves that membership out; the review
  // step then shows no notes rather than another business's.
  const membershipTags: Record<string, StylistTag[]> = {};
  await Promise.all(
    liveMemberships
      .filter((membership) => membership.businessId && membership.routes.length > 0)
      .map(async (membership) => {
        if (membership.memberId === memberId) {
          const tags = userPage?.tags ?? memberDetails?.tags;
          if (tags !== undefined) membershipTags[memberId] = adaptMemberTags(tags);
          return;
        }
        try {
          const details = await OpeningsJson.getMemberDetails(membership.memberId);
          membershipTags[membership.memberId] = adaptMemberTags(details?.tags);
        } catch {
          // Absent, never another membership's tags.
        }
      }),
  );

  const userData = adaptMemberUserData(
    pathId,
    member,
    detail.schedule,
    detail.services,
    config,
    userPage,
    memberDetails,
  );
  const profileMemberships = [
    canonical,
    ...liveMemberships.filter(
      (membership) => membership.business !== canonicalBusiness,
    ),
  ];

  return {
    ...userData,
    schedules: buildMemberSchedules(
      profileMemberships.flatMap((membership) => membership.routes),
    ),
    membershipTags,
  };
};

/** Resolve membership context once for tenure and photo attribution. */
const getMemberProfileContext = async (
  memberId: string,
  options?: { businessId?: string; locationId?: string },
): Promise<{ salonId?: string; workingSince?: string | null }> => {
  let salonId = options?.businessId;
  try {
    if (!salonId) {
      const business = getBusinessForMemberId(memberId)
        ?? getConfigByLocationId(options?.locationId)?.business;
      if (!business) return {};
      salonId = (await resolveOpeningsBusiness(business)).id;
    }
    const { members } = await OpeningsJson.getTeamPage(salonId);
    return {
      salonId,
      workingSince: members.find((member) => member.id === memberId)?.workingSince ?? null,
    };
  } catch {
    // A missing roster must not discard the already resolved photo business.
    return { salonId };
  }
};

/** One canonical public profile for both website entry points. */
const getWebsiteMiniProfile = async (
  requestedMemberId: string,
  options: { businessId?: string; locationId?: string },
): Promise<UserExperienceResponse> => {
  const username = getEditionUserPathIdFromMemberId(requestedMemberId);
  const memberships = username ? getStylistMemberships(username) : [];
  const canonical = username ? getCanonicalBusinessForStylist(username) : undefined;
  const identity = memberships.find((item) => item.business === canonical);
  const memberId = identity?.memberId ?? requestedMemberId;
  const businessId = identity
    ? (await resolveOpeningsBusiness(identity.business)).id
    : (await getMemberProfileContext(memberId, options)).salonId;
  const [experience, categories, rosterRows] = await Promise.all([
    getOpeningsBackedStylistExperience(memberId, { businessId, photoSource: "portfolio", locationId: options.locationId }),
    businessId ? getCachedStylistPhotoCategories(businessId).catch(() => undefined) : undefined,
    memberships.length ? Promise.all(memberships.map(async (membership) => {
      try {
        const business = await resolveOpeningsBusiness(membership.business);
        const { members } = await OpeningsJson.getTeamPage(business.id);
        return members.find((member) => member.id === membership.memberId);
      } catch { return undefined; }
    })) : businessId ? OpeningsJson.getTeamPage(businessId)
      .then(({ members }) => [members.find((member) => member.id === memberId)])
      .catch(() => []) : [],
  ]);
  const canonicalRoster = rosterRows.find((member) => member?.id === memberId);
  return {
    ...experience,
    stats: { ...experience.stats, bio: experience.stats.bio || canonicalRoster?.bio?.trim() || undefined },
    profile: {
      memberId,
      locationNames: rosterRows.some(Boolean)
        ? [...new Set(rosterRows.flatMap((member) => member?.schedules.map((schedule) => schedule.title).filter(Boolean) ?? []))]
        : undefined,
      // The canonical roster's tags, the same copy the directory shows; the
      // membership's own tags when the roster read failed.
      tags: canonicalRoster
        ? adaptMemberTags(canonicalRoster.tags)
        : (experience.tags ?? []),
    },
    photos: experience.photos.map((photo) => ({
      ...photo,
      ...(categories ? { serviceIds: categories[memberId]?.[stylistPhotoKey(photo.src)] ?? [] } : {}),
    })),
  };
};

export const getOpeningsBackedStylistExperience = async (
  memberId: string,
  options?: { businessId?: string; deviceId?: string; locationId?: string; photoSource?: "portfolio" | "published"; profileView?: boolean },
): Promise<UserExperienceResponse> => {
  if (options?.profileView) return getWebsiteMiniProfile(memberId, options);
  const pathId = getEditionUserPathIdFromMemberId(memberId);
  // The same person has a separate member record in each business they work
  // at; the haircut count covers all of them.
  const otherMemberIds = (pathId ? getStylistMemberships(pathId) : [])
    .map((membership) => membership.memberId)
    .filter((id) => id !== memberId);
  const [details, portfolio, profileContext, otherHaircutCounts] = await Promise.all([
    OpeningsJson.getMemberDetails(memberId),
    options?.photoSource === "portfolio"
      ? OpeningsJson.getUserPage({ identifier: memberId, businessId: options.businessId })
          .catch(() => undefined)
      : undefined,
    options?.photoSource === "portfolio"
      ? getMemberProfileContext(memberId, options)
      : { salonId: options?.businessId, workingSince: undefined },
    Promise.all(
      otherMemberIds.map((id) =>
        OpeningsJson.getMemberDetails(id).then(
          (other) => other.stats?.totalHaircuts,
          () => undefined,
        ),
      ),
    ),
  ]);
  const { salonId, workingSince } = profileContext;
  const memberName = pathId ?? "Stylist";
  // Unknown when any business's count is unavailable, never a partial sum.
  const totalHaircuts =
    sumCounts([details.stats?.totalHaircuts, ...otherHaircutCounts]) ?? undefined;
  const salonPhotos = !portfolio && options?.businessId
    ? await OpeningsJson.getPhotos({
        businessId: options.businessId,
        memberId,
        deviceId: options.deviceId,
        limit: 100,
      })
    : null;
  // Keep this adapter safe during the Openings rollout. Older Openings
  // deployments ignore the additive `memberId` query and return the salon's
  // whole first page. Filtering again here prevents another stylist's work
  // from appearing in this profile; if this stylist is not on that page, the
  // member-details payload remains the honest legacy fallback.
  const memberPhotos = salonPhotos?.items.filter(
    (photo) => photo.user?.id === memberId,
  );
  const photos = portfolio
    ? adaptMemberPortfolio(portfolio.photos, memberName, salonId)
    : memberPhotos?.length
    ? memberPhotos.map((photo, index) => ({
        _id: photo.id,
        ...photoEngagementMetadata(photo, salonId),
        src: photo.src,
        alt: photo.alt || `${memberName} work photo ${index + 1}`,
        type: photo.type,
        likeCount: photo.likeCount ?? 0,
        liked: photo.liked ?? false,
      }))
    : (details.photos ?? []).map((photo, index) => ({
        _id: photo.url,
        ...photoEngagementMetadata(photo, salonId),
        src: photo.url,
        alt: `${memberName} work photo ${index + 1}`,
        type: photo.type === "video" ? ("video" as const) : ("image" as const),
      }));

  return {
    ...(portfolio ? { photoSource: "portfolio" as const } : {}),
    ...(workingSince !== undefined ? { workingSince } : {}),
    instagramHandle:
      (details.instagramHandle || portfolio?.instagramHandle)?.trim().replace(/^@+/, "") || undefined,
    tags: adaptMemberTags(details.tags ?? portfolio?.tags),
    photos,
    stats: {
      totalHaircuts,
      bio: details.bio?.trim() || portfolio?.bio?.trim() || undefined,
    },
    profileVideo: details.profileVideo,
    reviews: (details.reviews ?? [])
      .filter((review) => review.note?.trim().length)
      .map((review) => ({
        note: review.note,
        customerName: review.customerName ?? "",
        createdAt: review.submittedAt ?? "",
        time: 0,
        quality: 0,
        service: 0,
      })),
  };
};

export const getOpeningsBackedLocationOpenings = async (
  locationId: string,
  date: string,
  selectedServices: SelectedService[],
): Promise<LocationOpeningsData[]> => {
  const config = getConfigByLocationId(locationId);
  if (!config) return [];

  const resolution = await getLiveLocationRoute(config);
  if (!resolution.route || !resolution.businessId) {
    if (resolution.hadFailure) {
      throw new Error("Unable to load availability. Please try again.");
    }
    return [];
  }
  const { route } = resolution;
  const detail = route.detail;
  const services = getServiceSelection(detail.services, selectedServices);

  if (services.length === 0) {
    if (selectedServices.length > 0) {
      throw new LocationServiceSelectionError();
    }
    return [];
  }

  const members = await OpeningsJson.getMemberOpenings({
    businessId: resolution.businessId,
    scheduleId: route.scheduleId,
    date,
    services,
  });
  const servicesById = new Map(
    detail.services.map((service) => [service.id, service]),
  );
  const liveRoster = new Set(detail.members.map(getOpeningsMemberId));

  return members
    .filter((member) =>
      liveRoster.has(
        member.teamMemberId ?? member.userId ?? member.username,
      ),
    )
    .map((member) =>
      adaptOpeningsMember(member, config, route.scheduleId, servicesById),
    );
};

type FanOutEntry = {
  item: OpeningsNextAvailabilityItem;
  route: LiveLocationRoute;
  memberId: string;
  services: SelectedService[];
};

const resolveMemberSelection = (
  services: OpeningsService[],
  memberId: string,
  selectedServices: SelectedService[],
): SelectedService[] | undefined => {
  const byId = new Map(services.map((service) => [service.id, service]));
  const resolved = selectedServices.map((selected) => {
    const match = findSelectedService(services, selected, memberId);
    if (!match) return undefined;
    const option = selected.option
      ? findSelectedOption(match, selected.option)
      : undefined;
    if (selected.option && !option) return undefined;
    const parent = match.referenceId ? byId.get(match.referenceId) : undefined;
    return {
      ...adaptService(match, parent),
      duration: match.durationMinutes ?? match.duration,
      ...(option ? { option: adaptOption(option) } : {}),
    } satisfies SelectedService;
  });

  return resolved.every(Boolean) ? (resolved as SelectedService[]) : undefined;
};

const selectionDuration = (services: SelectedService[]) =>
  services.reduce(
    (total, service) => total + (service.option?.duration ?? service.duration),
    0,
  );

/** Query each business identity once, using only roster-verified live routes. */
const fanOutStylistAvailability = async (
  pathId: string,
  date: string,
  selectedServices: SelectedService[],
  days: number,
): Promise<FanOutEntry[]> => {
  const memberships = getStylistMemberships(pathId);
  if (memberships.length === 0) return [];

  let hadFailure = false;
  let hadEligibleSelection = false;
  let hadLiveMembership = false;

  const perBusiness = await Promise.all(
    memberships.map(async (membership) => {
      const live = await getLiveStylistRoutes(membership);
      hadFailure ||= live.hadFailure;
      if (!live.businessId || live.routes.length === 0) return [];
      hadLiveMembership = true;

      const byDuration = new Map<
        number,
        { route: LiveLocationRoute; services: SelectedService[] }[]
      >();

      for (const route of live.routes) {
        const services = resolveMemberSelection(
          route.detail.services,
          live.memberId,
          selectedServices,
        );
        if (!services) continue;
        const duration = selectionDuration(services);
        if (!Number.isFinite(duration) || duration <= 0) continue;
        hadEligibleSelection = true;
        const eligible = byDuration.get(duration) ?? [];
        eligible.push({ route, services });
        byDuration.set(duration, eligible);
      }

      const perDuration = await Promise.all(
        Array.from(byDuration.entries()).map(async ([duration, eligible]) => {
          const routes = eligible.map((entry) => entry.route);
          try {
            const items = await OpeningsJson.getNextAvailability({
              businessId: live.businessId!,
              scheduleIds: routes.map((route) => route.scheduleId),
              teamMemberId: live.memberId,
              date,
              serviceDuration: duration,
              days,
              // The member's own clones for this selection, so Openings
              // leaves out the starts inside their minimum notice instead
              // of offering one it would then refuse.
              serviceIds: [
                ...new Set(
                  eligible.flatMap((entry) =>
                    entry.services.map((service) => service._id),
                  ),
                ),
              ],
            });

            return items.flatMap((item) => {
              const route = resolveOpeningRoute(item.schedule, routes);
              if (!route) {
                hadFailure = true;
                return [];
              }
              const services = eligible.find(
                (entry) => entry.route === route,
              )?.services;
              return services
                ? [{ item, route, memberId: live.memberId, services }]
                : [];
            });
          } catch (error) {
            console.error(error);
            hadFailure = true;
            return [] as FanOutEntry[];
          }
        }),
      );

      return perDuration.flat();
    }),
  );

  const entries = perBusiness.flat();
  // If every successfully refreshed membership rejected the server-rendered
  // service/option selection, this is catalog drift rather than a stylist
  // being fully booked. Do not throw this deterministic diagnosis when any
  // catalog failed to load, because a failed source may still have matched.
  if (
    selectedServices.length > 0 &&
    hadLiveMembership &&
    !hadEligibleSelection &&
    !hadFailure
  ) {
    throw new LocationServiceSelectionError();
  }
  const hasActualOpening = entries.some((entry) =>
    entry.item.openings.some((time) => time.trim().length > 0),
  );
  if (!hasActualOpening && hadFailure) {
    throw new Error("Unable to load availability. Please try again.");
  }
  return entries;
};

export const getOpeningsBackedUserOpenings = async (
  pathId: string,
  date: string,
  services: SelectedService[],
): Promise<UserOpeningsData[]> => {
  const results = await fanOutStylistAvailability(pathId, date, services, 1);
  return results
    .filter((entry) => entry.item.date === date)
    .map((entry) =>
      adaptUserOpenings(
        entry.item,
        entry.route,
        entry.memberId,
        entry.services,
      ),
    );
};

export const getOpeningsBackedUserNextOpenings = async (
  pathId: string,
  date: string,
  services: SelectedService[],
): Promise<(UserOpeningsData & { date: string })[]> => {
  const results = await fanOutStylistAvailability(pathId, date, services, 90);
  return results
    .map((entry) => ({
      ...adaptUserOpenings(
        entry.item,
        entry.route,
        entry.memberId,
        entry.services,
      ),
      date: entry.item.date,
    }))
    .sort(
      (a, b) =>
        a.date.localeCompare(b.date) ||
        (a.openings[0] ?? "").localeCompare(b.openings[0] ?? "") ||
        a.schedule.locationId.localeCompare(b.schedule.locationId) ||
        a.schedule._id.localeCompare(b.schedule._id),
    );
};

const getRescheduleLookupPurpose = (businessId: string, customerId: string) =>
  `rescheduleLookup:${businessId}:${customerId}`;

const resolveCustomerGateForBooking = async (
  businessId: string,
  customerId: string | undefined,
  teamMemberId?: string,
): Promise<OpeningsCustomerGate | undefined> => {
  if (!customerId) return undefined;
  try {
    const { gate } = await OpeningsJson.getCustomerGate({
      businessId,
      customerId,
      teamMemberId,
    });
    return gate;
  } catch (e) {
    console.error(e);
    return undefined;
  }
};

/**
 * Re-resolve the customer gate for a known customer. Used to enforce the
 * booking block at later steps (e.g. the reschedule scene) where the customer
 * may have bypassed the initial phone-lookup gate check.
 */
export const getOpeningsBackedCustomerGate = async (
  customerId: string,
  // Required, not optional: this read decides whether a deposit is owed, and
  // an absent member used to resolve Eddie's silently. A caller that has no
  // member must fail to compile, not degrade to "not blocked" at runtime.
  teamMemberId: string,
  appointment?: OpeningsAppointmentContext,
): Promise<OpeningsCustomerGate | undefined> => {
  const business = await resolveOpeningsBusiness(
    requireBusinessForAppointment({ ...appointment, userId: teamMemberId }),
  );
  return resolveCustomerGateForBooking(business.id, customerId, teamMemberId);
};

export const BOOKING_BLOCKED_MESSAGE =
  "We are not able to proceed. Please contact the business.";

const isCustomerBlockedError = (error: any) =>
  error?.status === 403 || error?.body?.error?.code === "CUSTOMER_BLOCKED";

const mapBlockedError = (error: any) =>
  isCustomerBlockedError(error) ? new Error(BOOKING_BLOCKED_MESSAGE) : error;

export const createOpeningsBackedPaymentMethod = async (
  args: ICreatePaymentMethodArgs,
  userId: string,
  appointment?: OpeningsAppointmentContext,
): Promise<{ success: boolean }> => {
  const business = await resolveOpeningsBusiness(
    requireBusinessForAppointment({ ...appointment, userId }),
  );
  const customer = await OpeningsJson.createCustomer({
    businessId: business.id,
    userId,
    firstName: args.firstName || "Unknown",
    lastName: args.lastName || "Unknown",
    phoneNumber: normalizePhoneNumber(args.phoneNumber),
  });
  await OpeningsJson.createPaymentMethodForBooking({
    customerId: customer.customerId,
    paymentId: args.paymentId,
  });
  return { success: true };
};

const adaptAppointmentHistory = (
  history: OpeningsAppointmentHistory,
  businessId: string,
  verificationPurpose?: string,
  gate?: OpeningsCustomerGate,
): AppointmentHistoryData => ({
  isNewClient: !history.customerId,
  hasUpcomingAppointments: history.hasUpcomingAppointments,
  verificationPurpose:
    verificationPurpose ??
    history.verificationPurpose ??
    (history.customerId && history.hasUpcomingAppointments
      ? getRescheduleLookupPurpose(businessId, history.customerId)
      : undefined),
  upcomingAppointments: history.upcomingAppointments.map((appointment) => ({
    _id: appointment.appointmentId ?? appointment.id ?? "",
    userId: "",
    date: appointment.date,
    time: appointment.time,
    datetime: new Date(
      appointment.datetime ?? `${appointment.date}T${appointment.time}`,
    ),
    serviceTitle:
      appointment.serviceTitle ?? appointment.services?.[0]?.title ?? undefined,
    locationName: appointment.locationName ?? undefined,
    stylistName: appointment.memberName ?? undefined,
  })),
  client: history.customerId
    ? {
        customerId: history.customerId,
        firstName: "Unknown",
        lastName: "Unknown",
        blocked: false,
        bookingBlocked: gate?.block ?? false,
        depositRequired: gate?.depositRequired ?? false,
      }
    : null,
});

export const getOpeningsBackedAppointmentHistory = async (
  phoneNumber: string,
  teamMemberId?: string,
  appointment?: OpeningsAppointmentContext,
): Promise<AppointmentHistoryData> => {
  // `teamMemberId` is forwarded to the deposit-gate read below, so resolving
  // the wrong business here is the silent-bypass case: no Customer row, no
  // gate, `depositRequired: false`.
  const business = await resolveOpeningsBusiness(
    resolveBusinessForContext({
      ...appointment,
      userId: teamMemberId ?? appointment?.userId,
    }),
  );
  const normalizedPhoneNumber = normalizePhoneNumber(phoneNumber);
  const history = await OpeningsJson.getAppointmentHistory({
    businessId: business.id,
    phoneNumber: normalizedPhoneNumber,
  });
  const bookingVerificationPurpose = `booking:${business.id}`;
  const shouldSendBookingVerification = !history.customerId;

  if (shouldSendBookingVerification) {
    await OpeningsJson.sendVerification({
      businessId: business.id,
      phoneNumber: normalizedPhoneNumber,
      purpose: bookingVerificationPurpose,
    });
  }

  const gate = await resolveCustomerGateForBooking(
    business.id,
    history.customerId,
    teamMemberId,
  );

  return adaptAppointmentHistory(
    history,
    business.id,
    shouldSendBookingVerification ? bookingVerificationPurpose : undefined,
    gate,
  );
};

export const getOpeningsBackedVerifiedAppointmentHistory = async (
  phoneNumber: string,
  code: string,
  teamMemberId?: string,
  appointment?: OpeningsAppointmentContext,
): Promise<AppointmentHistoryData> => {
  // Same deposit-gate read as `getOpeningsBackedAppointmentHistory`.
  const business = await resolveOpeningsBusiness(
    resolveBusinessForContext({
      ...appointment,
      userId: teamMemberId ?? appointment?.userId,
    }),
  );
  const history = await OpeningsJson.getVerifiedAppointmentHistory({
    businessId: business.id,
    phoneNumber: normalizePhoneNumber(phoneNumber),
    code,
  });

  const gate = await resolveCustomerGateForBooking(
    business.id,
    history.customerId,
    teamMemberId,
  );

  return adaptAppointmentHistory(history, business.id, undefined, gate);
};

export const sendOpeningsBackedVerificationCode = async (
  phoneNumber: string,
  purpose?: string,
  appointment?: OpeningsAppointmentContext,
) => {
  // Must resolve the SAME business as `verifyOpeningsBackedVerificationCode`
  // below: the `booking:${business.id}` purpose is what pairs the two, so a
  // code sent under one business can never be verified under another.
  const business = await resolveOpeningsBusiness(
    resolveBusinessForContext(appointment),
  );
  const normalizedPhoneNumber = normalizePhoneNumber(phoneNumber);
  const rescheduleLookupPurpose = purpose?.startsWith("rescheduleLookup:")
    ? purpose
    : undefined;

  if (rescheduleLookupPurpose) {
    // Best-effort: clear any prior reschedule-lookup session so the resend
    // cooldown does not block a fresh code. A cancel failure must never
    // prevent the new code from being sent.
    try {
      await OpeningsJson.cancelVerification({
        phoneNumber: normalizedPhoneNumber,
        purpose: rescheduleLookupPurpose,
      });
    } catch {
      // ignore — proceed to send a new verification code
    }
  }

  return OpeningsJson.sendVerification({
    businessId: business.id,
    phoneNumber: normalizedPhoneNumber,
    purpose,
  });
};

export const verifyOpeningsBackedVerificationCode = async (
  phoneNumber: string,
  code: string,
  appointment?: OpeningsAppointmentContext,
): Promise<boolean> => {
  // Pairs with `sendOpeningsBackedVerificationCode` — see its comment.
  const business = await resolveOpeningsBusiness(
    resolveBusinessForContext(appointment),
  );
  const result = await OpeningsJson.verifyCode({
    businessId: business.id,
    phoneNumber: normalizePhoneNumber(phoneNumber),
    code,
  });

  return result.verified;
};

export const createOpeningsBackedAppointment = async (
  args: ICreateAppointmentArgs,
): Promise<OpeningsCreateAppointmentResult> => {
  const businessKey = requireBusinessForAppointment(args);
  const business = await resolveOpeningsBusiness(businessKey);
  const customer = await OpeningsJson.createCustomer({
    businessId: business.id,
    userId: args.userId,
    firstName: args.firstName ?? "Unknown",
    lastName: args.lastName ?? "Unknown",
    phoneNumber: normalizePhoneNumber(args.phoneNumber),
  });
  const services = await getOpeningsAppointmentServices(args.scheduleId, args);
  const result = await OpeningsJson.createAppointment({
    businessId: business.id,
    userId: args.userId,
    scheduleId: args.scheduleId,
    customerId: customer.customerId,
    teamMemberId: args.userId,
    services,
    date: args.date,
    time: args.time,
    verificationCode: args.code,
    skipPaymentCollection: false,
    paymentMethodId: args.paymentMethodId,
    metadata: {
      sourceApp: "edition-client",
      sourceFlow: "openings-api-adapter",
      migrationLocation: args.locationId ?? args.scheduleId,
      openingsBusinessId: business.id,
      openingsBusinessSlug: EDITION_BUSINESS_HANDLES[businessKey],
      openingsScheduleId: args.scheduleId,
      openingsTeamMemberId: args.userId,
      openingsCustomerId: customer.customerId,
      customerState: customer.alreadyExists
        ? "existing-for-business"
        : "created-for-business",
      serviceIds: services.map((service) => service.id).join(","),
    },
  }).catch((e) => {
    throw mapBlockedError(e);
  });

  return {
    appointmentId: result.appointmentId,
    ...(result.conversion ? { conversion: result.conversion } : {}),
  };
};

export const rescheduleOpeningsBackedAppointment = async (
  args: IRescheduleAppointmentArgs,
): Promise<{ appointmentId: string }> => {
  const businessKey = requireBusinessForAppointment(args);
  const business = await resolveOpeningsBusiness(businessKey);
  const services = await getOpeningsAppointmentServices(args.scheduleId, args);
  let result: Awaited<ReturnType<typeof OpeningsJson.rescheduleAppointment>>;
  try {
    result = await OpeningsJson.rescheduleAppointment({
      rescheduleAppointmentId: args.rescheduleAppointmentId,
      businessId: business.id,
      userId: args.userId,
      scheduleId: args.scheduleId,
      services,
      date: args.date,
      time: args.time,
      verificationCode: args.verificationCode,
      phoneNumber: args.phoneNumber
        ? normalizePhoneNumber(args.phoneNumber)
        : undefined,
      paymentMethodId: args.paymentMethodId,
      requestId: args.requestId,
      metadata: {
        sourceApp: "edition-client",
        sourceFlow: "openings-api-adapter",
        migrationLocation: args.locationId ?? args.scheduleId,
        openingsBusinessId: business.id,
        openingsBusinessSlug: EDITION_BUSINESS_HANDLES[businessKey],
        openingsScheduleId: args.scheduleId,
        openingsTeamMemberId: args.userId,
        previousAppointmentId: args.rescheduleAppointmentId,
        serviceIds: services.map((service) => service.id).join(","),
      },
    });
  } catch (error) {
    // A reschedule only moves the time (booking standard BF-DEC-010). Say why
    // Openings refused and that the appointment is unchanged, instead of its
    // raw message; anything else keeps propagating unchanged.
    const refusal = rescheduleRefusalFromError(error);
    if (refusal) throw new Error(RESCHEDULE_REFUSAL_MESSAGES[refusal]);
    throw error;
  }

  return { appointmentId: result.appointmentId };
};

type OpeningsReviewService = NonNullable<
  OpeningsAppointmentReview["services"]
>[number];

/**
 * One review service line, as the stylist priced it. The option is carried
 * only when the snapshot has real option data beyond the templated `id`
 * (see `OpeningsAppointmentReview.services[].option`); `up` is passed through
 * untouched so an API that predates the flag yields `undefined`, never a
 * synthesized `+`.
 */
const adaptReviewService = (
  service: OpeningsReviewService,
): AppointmentReviewService => {
  const option = service.option;
  const hasOptionData = Boolean(
    option &&
    (option.title ||
      option.description ||
      typeof option.price === "number" ||
      typeof option.duration === "number"),
  );

  return {
    id: service.id,
    title: service.title ?? "",
    price: typeof service.price === "number" ? service.price : null,
    duration: service.durationMinutes ?? service.duration ?? 0,
    up: service.up,
    description: service.description ?? undefined,
    option:
      hasOptionData && option
        ? {
            id: option.id ?? undefined,
            title: option.title ?? undefined,
            description: option.description ?? undefined,
            price: typeof option.price === "number" ? option.price : undefined,
            duration:
              typeof option.duration === "number" ? option.duration : undefined,
          }
        : undefined,
  };
};

export const getOpeningsBackedAppointmentReview = async (
  appointmentId: string,
): Promise<AppointmentReviewData> => {
  const review = await OpeningsJson.getAppointmentReview(appointmentId);
  // The review payload carries no tags; the member's public details do
  // (`GET /v1/locations/users/:id`, data-cached). An enrichment only: a
  // failed read leaves the field absent and the confirmation renders
  // without the "Before your visit" block, never without the appointment.
  const memberTags = review.teamMemberId
    ? await OpeningsJson.getMemberDetails(review.teamMemberId)
        .then((details) => adaptMemberTags(details?.tags))
        .catch((error: unknown) => {
          console.error(
            "[booking] member tags lookup failed for the confirmation page",
            error,
          );
          return undefined;
        })
    : undefined;
  const appointmentContext = {
    userId: review.teamMemberId,
    locationId: review.schedule?.id,
    locationName: review.schedule?.title,
  };
  // Resolve through the same identifier chain every other appointment-shaped
  // lookup uses, not the schedule title alone. The title is the weakest link
  // — "Edition Studio - SG" matches no registry key at all — and it used to
  // be the only one, so a studio booking fell through to OPENINGS_LOCATIONS[0]
  // and was shown Pasadena's phone number and address.
  const config = getConfigForAppointment(appointmentContext);
  const identifierBusinesses = resolveAppointmentBusinesses(appointmentContext);
  // The review row's salonId is the system-of-record scope. Resolve it whenever
  // it is present, even when registry identifiers also resolve: those sources
  // must corroborate salonId, never silently override it.
  const salonBusinessKey = review.salonId
    ? await businessKeyForSalonId(review.salonId)
    : undefined;
  // A first-match config is insufficient here: the member may agree with the
  // review's salonId while the schedule id/title resolves to another salon.
  // Inspect every resolvable identifier and distrust all contact/location
  // metadata when even one contradicts the authoritative review row.
  const hasBusinessConflict = Boolean(
    salonBusinessKey &&
    identifierBusinesses.some((business) => business !== salonBusinessKey),
  );
  const trustedConfig = hasBusinessConflict ? undefined : config;
  // Empty, never another location's. A confirmation page showing the wrong
  // salon's phone number is worse than showing none: it sends the customer to
  // a salon that has no record of their appointment.
  const fallback = trustedConfig?.fallback ?? EMPTY_LOCATION_FALLBACK;
  const primaryService = review.services?.[0];
  const services = (review.services ?? []).map(adaptReviewService);
  const serviceName =
    review.serviceTitle ??
    review.services
      ?.map((service) => service.title)
      .filter(Boolean)
      .join(", ") ??
    "Appointment";

  // Same principle as the location fallback above, applied to the brand: a
  // studio member missing from the stylist map is deliberately bookable (the
  // write path resolves via locationId), and their review payload matches no
  // registry key. The wire's own salonId is always the authoritative scope
  // when present; only legacy payloads without it may fall back to the schedule
  // title's brand prefix and then Eddie's for a calendar invite.
  const businessKey =
    salonBusinessKey ??
    config?.business ??
    businessKeyForScheduleTitle(review.schedule?.title) ??
    DEFAULT_EDITION_BUSINESS;

  return {
    businessName: BUSINESSES[businessKey].branding.displayName,
    appointment: {
      customerId: review.customerId,
      datetime: new Date(review.startsAt ?? review.datetime),
      isCanceled: review.status === "CANCELED",
    },
    user: {
      name: review.teamMemberName ?? review.userName ?? "Stylist",
      phone: fallback.phone,
    },
    service: {
      name: serviceName,
      price: String(review.price ?? primaryService?.price ?? ""),
      duration:
        review.durationMinutes ??
        primaryService?.durationMinutes ??
        primaryService?.duration ??
        0,
      description: primaryService?.description ?? undefined,
    },
    services,
    location: {
      address: hasBusinessConflict
        ? ""
        : (review.schedule?.address ?? fallback.address),
      parking:
        !hasBusinessConflict && review.schedule?.parking
          ? {
              map: review.schedule.parking.link ?? "",
              description: review.schedule.parking.description ?? "",
            }
          : undefined,
    },
    ...(memberTags ? { memberTags } : {}),
  };
};

export const addOpeningsBackedAppointmentNote = async (
  appointmentId: string,
  customerId: string | undefined,
  note: string,
) => {
  if (!customerId) {
    throw new Error("Unable to save note for this appointment.");
  }

  return OpeningsJson.addAppointmentNote({ appointmentId, customerId, note });
};

export const createOpeningsBackedServiceRequest = async (
  args: IServiceRequestArgs,
) => {
  const business = await resolveOpeningsBusiness(
    requireBusinessForAppointment(args),
  );

  // Eddie's Openings stylists are always team members, so the member id is a
  // teamMemberId — mirror the booking flow (createOpeningsBackedAppointment),
  // which always passes teamMemberId. Sending it as `userId` makes Openings
  // look up an owner and fail with "User not found".
  return OpeningsJson.createServiceRequest({
    businessId: business.id,
    teamMemberId: args.userId,
    scheduleId: args.scheduleId,
    firstName: args.firstName,
    lastName: args.lastName,
    email: args.email,
    // Openings types this `z.string().min(1).max(30).optional()`: omitting it
    // is valid, but an EMPTY STRING is rejected as invalid input. The
    // consultation form now requires a phone number, but this adapter still
    // guards the wire contract for any caller that omits one — a blank must
    // be omitted rather than sent as "". `JSON.stringify` drops `undefined`
    // keys, which is what makes the omission actually reach the API.
    phoneNumber: args.phoneNumber?.trim() || undefined,
    note: args.note,
    userPhotos: args.userPhotos,
    referencePhotos: args.referencePhotos,
    serviceIds: args.serviceIds,
    serviceTitles: args.serviceTitles,
  });
};
